import React, { useMemo, useState } from 'react'
import { arc } from 'd3'

export interface GrowthWheelGraphSubCompetency {
  id: string | number
  name: string
}

export interface GrowthWheelGraphArea {
  id: string | number
  name: string
  subCompetencies: GrowthWheelGraphSubCompetency[]
}

interface GrowthWheelGraphProps {
  areas: GrowthWheelGraphArea[]
  title?: string
  className?: string
  sideContent?: React.ReactNode
}

interface GrowthWheelAreaArc extends GrowthWheelGraphArea {
  color: string
  startAngle: number
  endAngle: number
}

const WHEEL_SIZE = 700
const COMPETENCY_OUTER_RADIUS = 200
const SUB_INNER_RADIUS = 205
const SUB_OUTER_RADIUS = 315
const OUTER_INNER_RADIUS = 308
const OUTER_OUTER_RADIUS = 345
const COMPETENCY_LABEL_RADIUS = 135
const SUB_COMPETENCY_LABEL_RADIUS = 260
const WHEEL_START_ANGLE = -Math.PI
const AREA_LABEL_LINE_CHARS = 16
const SUB_COMPETENCY_LABEL_LINE_CHARS = 10
const BASE_LABEL_AREA_COUNT = 4
const MAX_AREA_LABEL_FONT_SIZE = 12
const MIN_AREA_LABEL_FONT_SIZE = 8
const MAX_SUB_LABEL_FONT_SIZE = 11
const MIN_SUB_LABEL_FONT_SIZE = 7
const AREA_COLORS = [
  '#FFBEB3',
  '#FFADAD',
  '#9BF6FF',
  '#BDB2FF',
  '#FDFFB6',
  '#FFD6A5',
  '#FFC6FF',
  '#D4F2A2',
  '#D9C3F0',
  '#D3DAE0',
  '#FFA0B2',
  '#FFA0BA',
  '#A0F3BF',
  '#D89EFF',
  '#99B2DD',
  '#8ACBFF',
]

const createArcPath = (
  startAngle: number,
  endAngle: number,
  innerRadius: number,
  outerRadius: number,
) =>
  arc()({
    startAngle,
    endAngle,
    innerRadius,
    outerRadius,
  }) ?? ''

const getPointOnWheel = (angle: number, radius: number) => ({
  x: Math.sin(angle) * radius,
  y: -Math.cos(angle) * radius,
})

const wrapWheelLabel = (label: string, maxLineChars: number) => {
  const words = label.split(' ')
  const lines: string[] = []
  let currentLine = ''

  words.forEach(word => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word

    if (nextLine.length > maxLineChars && currentLine) {
      lines.push(currentLine)
      currentLine = word
      return
    }

    currentLine = nextLine
  })

  if (currentLine) lines.push(currentLine)

  return lines
}

const getScaledFontSize = (
  areaCount: number,
  maxSize: number,
  minSize: number,
) => {
  const extraAreas = Math.max(0, areaCount - BASE_LABEL_AREA_COUNT)
  return Math.max(minSize, maxSize - extraAreas * 0.5)
}

const getWheelLabelFontStyles = (areaCount: number) =>
  ({
    '--gw-area-label-font-size': `${getScaledFontSize(
      areaCount,
      MAX_AREA_LABEL_FONT_SIZE,
      MIN_AREA_LABEL_FONT_SIZE,
    )}px`,
    '--gw-sub-label-font-size': `${getScaledFontSize(
      areaCount,
      MAX_SUB_LABEL_FONT_SIZE,
      MIN_SUB_LABEL_FONT_SIZE,
    )}px`,
  } as React.CSSProperties)

const getGrowthWheelAreaArcs = (
  areas: GrowthWheelGraphArea[],
): GrowthWheelAreaArc[] => {
  if (areas.length === 0) return []

  const wheelStartAngle = WHEEL_START_ANGLE / areas.length
  const wheelEndAngle = Math.PI * 2 + wheelStartAngle
  const sweep = (wheelEndAngle - wheelStartAngle) / areas.length

  return areas.map((area, index) => ({
    ...area,
    color: AREA_COLORS[index % AREA_COLORS.length],
    startAngle: wheelStartAngle + sweep * index,
    endAngle: wheelStartAngle + sweep * (index + 1),
  }))
}

const MultiLineText = ({
  lines,
  x,
  y,
  className,
}: {
  lines: string[]
  x: number
  y: number
  className: string
}) => {
  const firstLineOffset = lines.length > 1 ? -0.55 * (lines.length - 1) : 0

  return (
    <text x={x} y={y} textAnchor="middle" className={className}>
      {lines.map((line, index) => (
        <tspan
          key={`${line}-${index}`}
          x={x}
          dy={index === 0 ? `${firstLineOffset}em` : '1.1em'}
        >
          {line}
        </tspan>
      ))}
    </text>
  )
}

const GrowthWheelGraphSvg = ({ areas }: { areas: GrowthWheelGraphArea[] }) => {
  const areaArcs = useMemo(() => getGrowthWheelAreaArcs(areas), [areas])
  const labelFontStyles = useMemo(
    () => getWheelLabelFontStyles(areaArcs.length),
    [areaArcs.length],
  )
  const outerRingPath = useMemo(
    () =>
      createArcPath(-Math.PI, Math.PI, OUTER_INNER_RADIUS, OUTER_OUTER_RADIUS),
    [],
  )

  if (areaArcs.length === 0) {
    return (
      <div className="gw-d3-wheel__empty">
        Add competencies to preview the Growth Wheel graph.
      </div>
    )
  }

  return (
    <svg
      className="gw-d3-wheel__chart"
      style={labelFontStyles}
      viewBox={`${-WHEEL_SIZE / 2} ${
        -WHEEL_SIZE / 2
      } ${WHEEL_SIZE} ${WHEEL_SIZE}`}
      role="img"
      aria-label="Growth Wheel graph"
    >
      <path d={outerRingPath} fill="#4f4f4f" />

      {areaArcs.map(area => (
        <path
          key={`sub-ring-${area.id}`}
          d={createArcPath(
            area.startAngle,
            area.endAngle,
            SUB_INNER_RADIUS,
            SUB_OUTER_RADIUS,
          )}
          fill="#ffffff"
        />
      ))}

      {areaArcs.map(area => (
        <path
          key={`area-${area.id}`}
          d={createArcPath(
            area.startAngle,
            area.endAngle,
            0,
            COMPETENCY_OUTER_RADIUS,
          )}
          fill={area.color}
        />
      ))}

      {areaArcs.map(area => {
        const divider = getPointOnWheel(area.startAngle, OUTER_OUTER_RADIUS)

        return (
          <line
            key={`divider-${area.id}`}
            x1="0"
            y1="0"
            x2={divider.x}
            y2={divider.y}
            className="gw-d3-wheel__divider"
          />
        )
      })}

      {areaArcs.map(area => {
        const midAngle = (area.startAngle + area.endAngle) / 2
        const labelPoint = getPointOnWheel(midAngle, COMPETENCY_LABEL_RADIUS)

        return (
          <MultiLineText
            key={`area-label-${area.id}`}
            lines={wrapWheelLabel(area.name, AREA_LABEL_LINE_CHARS)}
            x={labelPoint.x}
            y={labelPoint.y}
            className="gw-d3-wheel__area-label"
          />
        )
      })}

      {areaArcs.flatMap(area => {
        const sweep = area.endAngle - area.startAngle
        const subCompetencies = area.subCompetencies

        return subCompetencies.map((subCompetency, index) => {
          const angle =
            area.startAngle + (sweep * (index + 0.5)) / subCompetencies.length
          const labelPoint = getPointOnWheel(angle, SUB_COMPETENCY_LABEL_RADIUS)

          return (
            <MultiLineText
              key={`sub-label-${area.id}-${subCompetency.id}`}
              lines={wrapWheelLabel(
                subCompetency.name,
                SUB_COMPETENCY_LABEL_LINE_CHARS,
              )}
              x={labelPoint.x}
              y={labelPoint.y}
              className="gw-d3-wheel__sub-label"
            />
          )
        })
      })}
    </svg>
  )
}

const GrowthWheelGraph = ({
  areas,
  title = 'Assessment Overview',
  className = '',
  sideContent,
}: GrowthWheelGraphProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const hasSideContent = Boolean(sideContent)

  return (
    <>
      <div
        className={`card shadow-sm border-0 rounded-3 gw-visualizer-card ${className}`}
      >
        <div className="card-body p-4 d-flex flex-column gap-3">
          <h5 className="mb-0 fw-semibold text-dark">{title}</h5>

          <div
            className={
              hasSideContent
                ? 'gw-d3-wheel__content gw-d3-wheel__content--with-side'
                : 'position-relative d-flex justify-content-center'
            }
          >
            <button
              type="button"
              onClick={() => setIsFullscreen(true)}
              className="p-0 border-0 bg-transparent gw-d3-wheel__button"
              aria-label="View Growth Wheel fullscreen"
            >
              <div className="gw-d3-wheel">
                <GrowthWheelGraphSvg areas={areas} />
              </div>
            </button>
            {sideContent && (
              <div className="gw-d3-wheel__side-content">{sideContent}</div>
            )}
          </div>
        </div>
      </div>

      {isFullscreen && (
        <div
          className="modal fade show d-block gw-d3-wheel__modal"
          role="dialog"
          tabIndex={-1}
          onClick={() => setIsFullscreen(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-xl"
            role="document"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-content border-0">
              <div className="modal-body p-3 position-relative">
                <button
                  type="button"
                  className="btn-close position-absolute end-0 mt-1 me-1"
                  aria-label="Close"
                  onClick={() => setIsFullscreen(false)}
                />
                <div className="gw-d3-wheel gw-d3-wheel--fullscreen">
                  <GrowthWheelGraphSvg areas={areas} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default GrowthWheelGraph
